import { EyeSlashIcon, FunnelIcon } from '@heroicons/react/20/solid';
import { useState } from 'react';

import {
  FilterOptions,
  QueueFilterValues,
  queueFilterCount,
} from '../api/types';
import QueueFilterBar from './QueueFilterBar';

// A queue's heading plus its Show/Hide pill and the approved filter bar the
// pill reveals (GRAVY-10315).
//
// One component rather than the same twenty lines in NoShowTab,
// MissingTimesheetTab and NeedsApprovalTab, because "layout and behaviour are
// consistent across all three queues" is an explicit acceptance criterion — and
// three copies is how they stop being consistent.
//
// Open/closed is local state, deliberately. The SELECTIONS live in the page (one
// set per queue, see index.tsx) so they survive a tab switch and never leak
// between queues; whether the bar happens to be expanded is a view preference,
// and collapsing it must not clear anything — hence the two are separate.

type Props = {
  heading: string;
  /** Queue key, for the ARIA relationship between pill and bar. */
  queue: string;
  options?: FilterOptions;
  value: QueueFilterValues;
  onChange: (next: QueueFilterValues) => void;
};

const QueueFilterHeader = ({
  heading,
  queue,
  options,
  value,
  onChange,
}: Props) => {
  const [open, setOpen] = useState(false);
  const count = queueFilterCount(value);
  const barId = `dashv2-${queue}-filters`;

  return (
    <>
      <div className="dashv2-queue-title">
        <h2 className="dashv2-queue-heading">{heading}</h2>
        <button
          type="button"
          className="dashv2-showbtn"
          aria-expanded={open}
          aria-controls={open ? barId : undefined}
          onClick={() => setOpen((o) => !o)}
        >
          {open ? <EyeSlashIcon /> : <FunnelIcon />}
          {/* Wrapped and width-reserved so the control does not resize as it
              toggles (GRAVY-10332): "Hide" renders ~7px narrower than "Show" at
              14px, and an unreserved label made the button jump on every click,
              shifting everything to its left. */}
          <span className="dashv2-showbtn-word">{open ? 'Hide' : 'Show'}</span>
          {/* The count stays on the pill so a COLLAPSED bar still says filters
              are in force — otherwise hiding them looks like clearing them. */}
          {count > 0 && <span className="dashv2-qfcount">{count}</span>}
        </button>
      </div>

      {open && (
        <div id={barId}>
          <QueueFilterBar
            queue={queue}
            options={options}
            value={value}
            onChange={onChange}
          />
        </div>
      )}
    </>
  );
};

export default QueueFilterHeader;
